import { useCallback, useEffect, useMemo, useState } from 'react';

import { PhysicianBioHero } from '@/design-system/molecules/PhysicianBioHero';
import { useGenericLists } from '@/hooks/useGenericLists';
import { arrayMatchValues } from '@/utils/arrayMatchValues';
import { formatPhoneNumber } from '@/utils/stringFormat';

/**
 * Lowercased literal a `specialtyServices` entry must equal exactly for the
 * provider to be treated as offering Virtual Care. Matching is case-insensitive
 * but NOT a substring check, so adjacent free-text entries (e.g.
 * "Non-Virtual Care Coordination") do not produce a false positive.
 */
const VIRTUAL_CARE_SERVICE = 'virtual care';

const IntegrationPhysicianBioHero = (props) => {
    const DEFAULT_FALLBACK_PHONE = '800-247-9580';
    const provider = props?.provider;
    const { data } = useGenericLists('provider-affiliations-badges');
    const specialtiesArray = provider?.specialties?.split(';');
    const practiceGroups = provider?.practiceGroups?.map((group) => group.name);
    const affiliationBadgeDictionary = useMemo(() => data, [data]);
    const languagesArray = provider?.language?.split(';');
    const phoneNumberFromLocation = provider?.locations?.find((location) =>
        location.phone?.trim(),
    )?.phone;
    const rawPhone =
        typeof phoneNumberFromLocation === 'string' && phoneNumberFromLocation.trim()
            ? phoneNumberFromLocation.trim()
            : DEFAULT_FALLBACK_PHONE;
    const phoneNumber = formatPhoneNumber(rawPhone);
    const additionalVisitTypes = provider?.additionalVisitTypes?.split(';');
    const specialtyServicesArray = provider?.specialtyServices?.split(';');
    const hasVirtualCare = Boolean(
        specialtyServicesArray?.some(
            (service) => service.trim().toLowerCase() === VIRTUAL_CARE_SERVICE,
        ),
    );
    const [badgeLabel, setBadgeLabel] = useState('');

    /**
     * Gets the provider affiliation label based on the
     * practiceGroups and affiliationBadgeDictionary arrays comparison
     *
     * 1. compare practiceGroups and affiliationBadgeDictionary
     * 2. filter the affiliationBadgeDictionary by using the affiliationBadge
     * 3. set the title value from the filtered array
     */
    const getAffiliationLabel = useCallback(() => {
        // 1. compare practiceGroups and affiliationBadgeDictionary thru arrayMatchValues method
        // to return the affiliationBadge array with a single value
        const affiliationBadge =
            practiceGroups?.length && affiliationBadgeDictionary?.length
                ? arrayMatchValues(
                      practiceGroups,
                      affiliationBadgeDictionary?.map((badge) => badge.value),
                  )
                : null;

        // 2. filter the affiliationBadgeDictionary by using the affiliationBadge
        if (affiliationBadge?.length) {
            let affiliationBadgeLabel = null;
            affiliationBadgeLabel = affiliationBadgeDictionary?.filter(
                (badge) => badge.value === affiliationBadge,
            );

            // 3. set the title value from the previous filtering
            if (affiliationBadgeLabel?.length) {
                const [{ title }] = affiliationBadgeLabel;
                setBadgeLabel(title);
            }
        } else {
            setBadgeLabel('');
        }
    }, [affiliationBadgeDictionary, practiceGroups]);

    useEffect(getAffiliationLabel, [getAffiliationLabel]);

    return (
        <PhysicianBioHero
            id={props?.id}
            breadcrumbs={props?.breadcrumbs}
            providerImage={provider?.image}
            acceptingNewPatients={provider?.acceptingNewPatients}
            onlineBooking={provider?.onlineBookingAvailable}
            hasVirtualCare={hasVirtualCare}
            additionalVisitTypes={additionalVisitTypes}
            providerName={provider?.title?.replace(',', ', ')}
            primarySpecialty={provider?.primarySpecialty}
            specialties={specialtiesArray}
            averageRating={props?.reviews?.overallRating}
            numberOfReviews={props?.reviews?.totalRatingCount}
            affiliationBadgeLabel={badgeLabel}
            languages={languagesArray}
            gender={provider?.gender}
            lgbtServices={provider?.lgbtServices}
            phoneNumber={phoneNumber}
            npi={provider?.npi}
            newPatientAppointmentIds={props?.newPatientAppointmentIds}
            appointmentUrl={props?.appointmentUrl}
            insurancesText={props?.insurancesText}
            insuranceLink={props?.insuranceLink}
            insurancesLinkText={props?.insurancesLinkText}
            bookAppointmentText={props?.bookAppointmentText}
            bookAppointmentAriaLabel={props?.bookAppointmentAriaLabel}
            availabilityText={props?.availabilityText}
            departmentIds={props?.departmentId}
            availableSoonOnline={provider?.availableSoonOnline}
            availableOnline={provider?.availableOnline}
            firstAvailabilityDate={provider?.firstAvailabilityDate}
            nextAvailabilityDate={provider?.nextAvailabilityDate}
            disableAvailabilityMessage={props?.disableAvailabilityMessage ?? false}
        />
    );
};

export default IntegrationPhysicianBioHero;
